/** Pure helpers behind an audit entry's Before / After comparison. */

import type { AuditEntryDetail } from '@/api/types';

export type Doc = Record<string, unknown>;

export function asDoc(value: unknown): Doc | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  return Object.fromEntries(Object.entries(value));
}

export function renderJson(value: unknown): string {
  if (value === undefined) return 'absent';
  if (value === null) return 'null';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export interface EntryDocs {
  context: Doc | null;
  before: Doc | null;
  after: Doc | null;
  /** Whether the action recorded a before or after document at all. */
  recorded: boolean;
  changed: Set<string>;
}

export function entryDocs(e: AuditEntryDetail): EntryDocs {
  const before = asDoc(e.before_value);
  const after = asDoc(e.after_value);
  const fields = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);
  const changed = new Set(
    [...fields].filter((f) => renderJson(before?.[f]) !== renderJson(after?.[f]) || (before === null) !== (after === null)),
  );
  return {
    context: asDoc(e.context),
    before,
    after,
    recorded: e.before_value !== null || e.after_value !== null,
    changed,
  };
}
